import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailWarning, CheckCircle2, Store, Package, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Loading } from "@/components/RequireAuth";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountrySelect, CountryNotice } from "@/components/CountrySelect";

const ProfileForm = ({ user, setUser }) => {
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", country_code: user.country_code || "SN" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setUser((await api.put("/auth/profile", f)).data); toast.success("Profil enregistré"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} data-testid="profile-form" className="grid gap-4 rounded-2xl border bg-white p-5 sm:grid-cols-2">
      <h2 className="font-display text-lg font-bold sm:col-span-2">Mon profil</h2>
      <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="profile-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
      <div className="space-y-1.5"><Label>Téléphone</Label><Input data-testid="profile-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Pays</Label><CountrySelect testid="profile-country" value={f.country_code} onChange={(v) => setF({ ...f, country_code: v })} /><CountryNotice code={f.country_code} /></div>
      <Button disabled={busy} className="rounded-full sm:col-span-2 sm:w-fit" data-testid="profile-save">Enregistrer</Button>
    </form>
  );
};

export default function Account() {
  const { user, setUser } = useAuth();
  const [link, setLink] = useState(null);
  if (!user) return <Loading />;
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      setLink(data.dev_verification_link);
      data.message ? toast.info(data.message) : toast.success("Nouveau lien de validation préparé");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="account-page" className="mx-auto max-w-3xl space-y-6 px-4 py-8 md:px-6">
      <h1 className="font-display text-3xl font-black tracking-tight">Mon compte</h1>
      <div className="rounded-2xl border bg-white p-5">
        <p className="font-display text-xl font-bold">{user.name}</p>
        <p className="text-gray-500">{user.email}</p>
        <p className="mt-2 text-sm">Rôle : <span data-testid="account-role" className="font-semibold">{{ customer: "Client", seller: "Vendeur", admin: "Administrateur" }[user.role]}</span></p>
        {user.email_verified ? (
          <p data-testid="email-verified" className="mt-3 flex items-center gap-2 text-sm font-semibold text-green-800"><CheckCircle2 className="h-4 w-4" />E-mail validé</p>
        ) : (
          <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
            <p className="flex items-center gap-2 font-semibold"><MailWarning className="h-4 w-4" />E-mail non validé</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={resend} data-testid="resend-verification">Renvoyer le lien</Button>
            {link && <Link to={link} data-testid="dev-link" className="ml-3 font-semibold underline">Mode test : valider maintenant</Link>}
          </div>
        )}
      </div>
      <ProfileForm user={user} setUser={setUser} />
      <div className="grid gap-3 sm:grid-cols-3">
        <Link to="/mes-commandes" className="rounded-xl border bg-white p-4 hover:border-orange-300" data-testid="account-orders"><Package className="mb-2 h-5 w-5 text-orange-500" /><p className="font-bold">Mes commandes</p></Link>
        {user.shop ? (
          <Link to="/vendeur" className="rounded-xl border bg-white p-4 hover:border-orange-300" data-testid="account-seller"><Store className="mb-2 h-5 w-5 text-green-800" /><p className="font-bold">Espace vendeur</p><StatusBadge status={user.shop.status} kind="shop" /></Link>
        ) : user.role !== "admin" && (
          <Link to="/devenir-vendeur" className="rounded-xl border bg-white p-4 hover:border-orange-300" data-testid="account-become-seller"><Store className="mb-2 h-5 w-5 text-green-800" /><p className="font-bold">Devenir vendeur</p></Link>
        )}
        {user.role === "admin" && <Link to="/admin" className="rounded-xl border bg-white p-4 hover:border-orange-300" data-testid="account-admin"><Shield className="mb-2 h-5 w-5 text-indigo-600" /><p className="font-bold">Administration</p></Link>}
      </div>
    </div>
  );
}
